// src/components/Settings/Layout/DeleteAccount.js

import React, { useState, useEffect, useContext } from "react";
import { useNavigate }                            from "react-router-dom";
import { FetchUserDataInSettings }                from "../Utils/SettingsUserFetchMockData";
import { logout }                                 from "../../../../Pages/PublicLayout/SigninPage/auth";
// import { ThemeContext }                           from "../../../../context/ThemeContext";
import "../Styles/DeleteAccount.css";

const DeleteAccount = () => {
  const navigate                        = useNavigate();
  // const { theme }                       = useContext(ThemeContext);
  const [profileData,    setProfileData]   = useState([]);
  const [loginInfo,      setLoginInfo]     = useState(null);
  const [confirmText,    setConfirmText]   = useState("");
  const [step,           setStep]          = useState(1); // 1: warning | 2: confirm | 3: final
  const [isDeleting,     setIsDeleting]    = useState(false);

  // ── Theme loader ───────────────────────────────────────────────
  // useEffect(() => {
  //   import(`../Styles/themes/${theme}/themes.css`)
  //     .then(() => console.log(`Loaded ${theme} styles for DeleteAccount`))
  //     .catch(() => console.error(`Theme CSS not found for ${theme}`));
  // }, [theme]);

  // ── Load user data ─────────────────────────────────────────────
  useEffect(() => {
    const rawLogin = localStorage.getItem("userLoggedin");
    if (rawLogin) {
      try {
        const parsed = JSON.parse(rawLogin);
        setLoginInfo(parsed);
      } catch (e) {
        console.error("Invalid login info in localStorage.");
      }
    }

    const fetchData = async () => {
      const userData = await FetchUserDataInSettings();
      if (userData && Array.isArray(userData.profiles)) {
        const profilesWithUUID = userData.profiles.map((profile, index) => ({
          profileUUID: `uuid-${profile.profilename}-${index}`,
          ...profile,
        }));
        setProfileData(profilesWithUUID);
      }
    };

    fetchData();
  }, []);

  // ── Derived values ─────────────────────────────────────────────
  const currentUser = (() => {
    if (!loginInfo?.login) return null;
    const users = JSON.parse(localStorage.getItem("users") || "[]");
    return users.find(u =>
      u.userId === loginInfo.login ||
      u.email  === loginInfo.login ||
      u.mobile === loginInfo.login
    ) || null;
  })();

  const linkedProfiles = currentUser
    ? profileData.filter(p => p.ownerId === currentUser.userId)
    : [];

  const isConfirmValid = confirmText.trim() === "DELETE";

  // ── Delete handler ─────────────────────────────────────────────
  const handleDeleteAccount = async () => {
    if (!isConfirmValid) return;
    if (!loginInfo?.login) { alert("No user is currently logged in."); return; }
    if (!currentUser)       { alert("User not found."); return; }

    setIsDeleting(true);

    try {
      // Remove user from 'users'
      const users = JSON.parse(localStorage.getItem("users") || "[]");
      const updatedUsers = users.filter(
        u =>
          u.userId !== currentUser.userId &&
          u.email  !== currentUser.email  &&
          u.mobile !== currentUser.mobile
      );
      localStorage.setItem("users", JSON.stringify(updatedUsers));

      // Clear session
      localStorage.removeItem("userLoggedin");
      localStorage.removeItem("session");

      // Short delay for UX
      await new Promise(res => setTimeout(res, 800));

      logout();
      navigate("/");
    } catch (err) {
      console.error("Failed to delete account:", err);
      setIsDeleting(false);
    }
  };

  return (
    <div className="st-panel da-root">
      <h2 className="st-panel-title da-title">🗑️ Delete Account</h2>
      <p className="st-panel-subtitle">
        Permanently delete your account and all associated data.{" "}
        <strong>This action is irreversible.</strong>
      </p>

      {/* ── STEP 1: Warning Overview ─────────────────────────────── */}
      {step === 1 && (
        <div className="da-step da-step--1">

          {/* Danger Banner */}
          <div className="da-danger-banner">
            <span className="da-danger-banner-icon">⚠️</span>
            <div>
              <p className="da-danger-banner-title">Before you proceed, read this carefully</p>
              <p className="da-danger-banner-desc">
                Deleting your account is <strong>permanent</strong>. All your data,
                content, and settings will be wiped immediately and cannot be recovered.
              </p>
            </div>
          </div>

          {/* Consequences List */}
          <div className="da-consequences">
            <h3 className="da-consequences-title">📋 What will be deleted:</h3>
            <ul className="da-consequences-list">
              {[
                { icon: "👤", text: "Your account and all personal information" },
                { icon: "🎭", text: `All ${linkedProfiles.length} linked profile(s) under your account` },
                { icon: "📸", text: "All posts, stories, and media content" },
                { icon: "💬", text: "All messages, comments, and interactions" },
                { icon: "💳", text: "Your active subscription (no refund issued)" },
                { icon: "🎁", text: "All reward coins and points earned" },
                { icon: "👥", text: "Your friends list and group memberships" },
              ].map((item, i) => (
                <li key={i} className="da-consequence-item">
                  <span className="da-consequence-icon">{item.icon}</span>
                  <span className="da-consequence-text">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Account Summary */}
          {currentUser && (
            <div className="da-account-summary">
              <h3 className="da-account-summary-title">🔍 Account to be deleted:</h3>
              <div className="da-account-summary-grid">
                <div className="da-account-summary-row">
                  <span className="da-account-summary-key">👤 User ID</span>
                  <span className="da-account-summary-val">{currentUser.userId}</span>
                </div>
                <div className="da-account-summary-row">
                  <span className="da-account-summary-key">📧 Email</span>
                  <span className="da-account-summary-val">{currentUser.email || "—"}</span>
                </div>
                <div className="da-account-summary-row">
                  <span className="da-account-summary-key">🎭 Profiles</span>
                  <span className="da-account-summary-val">{linkedProfiles.length} linked</span>
                </div>
              </div>
            </div>
          )}

          <div className="da-step-actions">
            <button
              className="da-btn da-btn--proceed"
              onClick={() => setStep(2)}
            >
              I understand, proceed →
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 2: Type Confirmation ─────────────────────────────── */}
      {step === 2 && (
        <div className="da-step da-step--2">
          <div className="da-confirm-box">
            <div className="da-confirm-icon">🔐</div>
            <h3 className="da-confirm-title">Confirm Account Deletion</h3>
            <p className="da-confirm-desc">
              To permanently delete your account, type{" "}
              <span className="da-confirm-keyword">DELETE</span>{" "}
              in the field below. This action cannot be undone.
            </p>

            <div className="da-confirm-input-wrapper">
              <input
                type="text"
                className={`da-confirm-input ${isConfirmValid ? "da-confirm-input--valid" : ""}`}
                placeholder="Type DELETE here"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                autoComplete="off"
                spellCheck="false"
              />
              {isConfirmValid && (
                <span className="da-confirm-input-check">✓</span>
              )}
            </div>

            {confirmText.length > 0 && !isConfirmValid && (
              <p className="da-confirm-hint">
                Keep typing — must match exactly: <strong>DELETE</strong>
              </p>
            )}
          </div>

          {/* Linked Profiles Warning */}
          {linkedProfiles.length > 0 && (
            <div className="da-profiles-warning">
              <span>⚠️</span>
              <p>
                <strong>{linkedProfiles.length} profile(s)</strong> will also be permanently deleted:
                {" "}
                {linkedProfiles.map((p, i) => (
                  <span key={i} className="da-profile-tag">
                    {p.profilename || p.profileUUID}
                    {i < linkedProfiles.length - 1 ? ", " : ""}
                  </span>
                ))}
              </p>
            </div>
          )}

          <div className="da-step-actions da-step-actions--split">
            <button
              className="da-btn da-btn--back"
              onClick={() => { setStep(1); setConfirmText(""); }}
            >
              ← Go back
            </button>
            <button
              className={`da-btn da-btn--delete ${!isConfirmValid ? "da-btn--disabled" : ""}`}
              onClick={isConfirmValid ? handleDeleteAccount : undefined}
              disabled={!isConfirmValid || isDeleting}
            >
              {isDeleting ? (
                <span className="da-btn-spinner">⏳ Deleting…</span>
              ) : (
                "🗑️ Delete My Account"
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteAccount;
